import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ThumbsUp } from "lucide-react";
import FeedbackStatusBadge from "./FeedbackStatusBadge";
import { type FeedbackStatus } from "@/types";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import FeedbackDetailDialog from "./FeedbackDetailDialog";

type FeedbackItemProps = {
  id: string;
  title: string;
  description: string;
  status: FeedbackStatus;
  votes: number;
  hasVoted: boolean;
  board_id?: string;
  created_at?: string;
  onVote: (id: string) => void;
  isAdmin?: boolean;
  companyStatuses?: any[];
  onStatusChange?: (id: string, status: FeedbackStatus) => void;
  onDelete?: (id: string) => void;
  boardName?: string;
  // New props for scoring
  scoreMethod?: 'value_effort' | 'rice';
  value_score?: number;
  effort_score?: number;
  reach_score?: number;
  impact_score?: number;
  confidence_score?: number;
  effort_rice_score?: number;
  onSaveScores?: (
    id: string, 
    data: {
      score_method: 'value_effort' | 'rice',
      value_score?: number,
      effort_score?: number,
      reach_score?: number,
      impact_score?: number,
      confidence_score?: number,
      effort_rice_score?: number,
    }
  ) => void;
};

const FeedbackItem = ({ 
  id, 
  title, 
  description, 
  status, 
  votes,
  hasVoted,
  created_at,
  onVote,
  isAdmin = false,
  companyStatuses = [],
  onStatusChange,
  onDelete,
  boardName,
  // New scoring props
  scoreMethod = 'value_effort',
  value_score = 5,
  effort_score = 5,
  reach_score = 5,
  impact_score = 5,
  confidence_score = 5,
  effort_rice_score = 5,
  onSaveScores,
}: FeedbackItemProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const formattedDate = created_at 
    ? format(new Date(created_at), 'd MMM')
    : null;

  return (
    <>
      <Card 
        className="overflow-hidden cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setDialogOpen(true)}
      >
        <div className="flex items-center p-4">
          <div className="flex items-center space-x-2 mr-2">
            <Button 
              variant={hasVoted ? "default" : "outline"}
              size="sm"
              className="w-16 flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onVote(id);
              }}
            >
              <ThumbsUp className="w-4 h-4 mr-2" />
              {votes}
            </Button>
          </div>
          
          <div className="flex-grow mx-2 font-medium truncate">
            {title}
          </div>
          
          <div className="flex items-center space-x-3 ml-auto">
            {boardName && (
              <Badge variant="outline" className="hidden sm:flex">
                {boardName}
              </Badge>
            )}
            
            {formattedDate && (
              <span className="text-xs text-muted-foreground hidden sm:block">{formattedDate}</span>
            )}
            
            <FeedbackStatusBadge status={status} companyStatuses={companyStatuses} />
          </div>
        </div>
      </Card>

      <FeedbackDetailDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        id={id}
        title={title}
        description={description}
        status={status}
        votes={votes}
        hasVoted={hasVoted}
        created_at={created_at}
        boardName={boardName}
        onVote={onVote}
        isAdmin={isAdmin}
        companyStatuses={companyStatuses}
        onStatusChange={onStatusChange}
        onDelete={onDelete}
        scoreMethod={scoreMethod}
        value_score={value_score}
        effort_score={effort_score}
        reach_score={reach_score}
        impact_score={impact_score}
        confidence_score={confidence_score}
        effort_rice_score={effort_rice_score}
        onSaveScores={onSaveScores}
      />
    </>
  );
};

export default FeedbackItem;
